import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Pagination,
  Skeleton,
  useTheme,
  useMediaQuery,
  Stack,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  People as PeopleIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import PermissionGuard from "../access-control/PermissionGuard";
import { useAccessControl } from "../../hooks/useAccessControl";
import { RESOURCES, ACTIONS } from "../../hooks/usePermissions";
import { useTeachers } from "../../hooks/useTeachers";
import { useTeacherFilters } from "../../hooks/useTeacherFilters";
import { useTeacherForm } from "../../hooks/useTeacherForm";
import FilterBar from "./FilterBar";
import TeachersTable from "./TeachersTable";
import FormDialog from "./FormDialog";
import TeacherForm from "./TeacherForm";
import TeacherDetailsDialog from "./TeacherDetailsDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

const TeachersRefactored = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.REPORT_CARD.TEACHERS, ACTIONS.READ);
  const canCreate = can(RESOURCES.REPORT_CARD.TEACHERS, ACTIONS.CREATE);

  const {
    teachers,
    loading,
    filtering,
    pagination,
    fetchTeachers,
    createTeacher,
    updateTeacher,
    deleteTeacher,
    getTeacherById,
  } = useTeachers();

  const {
    formData,
    setFormData,
    editingTeacher,
    showForm,
    openForm,
    resetForm,
    updateFormData,
  } = useTeacherForm();

  const [viewingTeacher, setViewingTeacher] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCreatedTeacherDialog, setShowCreatedTeacherDialog] = useState(false);
  const [createdTeacherCredentials, setCreatedTeacherCredentials] = useState({ loginId: '', loginType: '', password: '' });

  const handleFilterChange = useCallback((filters) => {
    fetchTeachers(1, filters, true);
  }, [fetchTeachers]);

  const {
    searchTerm,
    setSearchTerm,
    employmentStatusFilter,
    setEmploymentStatusFilter,
    activeFilter,
    setActiveFilter,
    clearFilters,
    hasActiveFilters,
  } = useTeacherFilters(handleFilterChange);

  useEffect(() => {
    if (canRead) fetchTeachers();
  }, [fetchTeachers, canRead]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingTeacher) {
        await updateTeacher(editingTeacher.id, formData);
        setSnackbar({ open: true, message: 'Teacher updated successfully!', severity: 'success' });
      } else {
        const tempPassword = formData.password;
        await createTeacher(formData);
        
        // Show credentials dialog with phone number
        setCreatedTeacherCredentials({
          loginId: formData.phone_number,
          loginType: 'Phone Number',
          password: tempPassword
        });
        setShowCreatedTeacherDialog(true);
        setSnackbar({ open: true, message: 'Teacher created successfully!', severity: 'success' });
      }
      
      fetchTeachers(pagination.current_page);
      resetForm();
    } catch (error) {
      console.error("Error saving teacher:", error);
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
        setSnackbar({ open: true, message: errorMessages, severity: 'error' });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.message || error.message, severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleView = async (teacher) => {
    setViewingTeacher(teacher);
    try {
      const response = await getTeacherById(teacher.id);
      setViewingTeacher(response.data);
    } catch (error) {
      console.error("Error fetching teacher details:", error);
    }
  };

  const handleDelete = async (id) => {
    setTeacherToDelete(teachers.find(t => t.id === id));
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteTeacher(teacherToDelete.id);
      fetchTeachers(pagination.current_page);
      setSnackbar({ open: true, message: 'Teacher deleted successfully!', severity: 'success' });
    } catch (error) {
      console.error("Error deleting teacher:", error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to delete teacher', severity: 'error' });
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setTeacherToDelete(null);
    }
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view teachers.</Alert>;

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={200} height={20} />
          </Box>
          <Skeleton variant="rectangular" width={140} height={36} sx={{ borderRadius: 1 }} />
        </Box>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {[...Array(4)].map((_, i) => (
                <Grid item xs={12} md={3} key={i}>
                  <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Skeleton variant="text" width={120} height={24} sx={{ mb: 2 }} />
            <Paper elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    {[...Array(5)].map((_, i) => (
                      <TableCell key={i}>
                        <Skeleton variant="text" width={80} height={16} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(8)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(5)].map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" width={80} height={16} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={4}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>
              Teachers Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage teacher records and allocations
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent={{ xs: "stretch", md: "flex-end" }} mt={{ xs: 2, md: 0 }}>
              {canCreate && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => openForm()}
                  size="small"
                  fullWidth={isMobile}
                >
                  Add Teacher
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        employmentStatusFilter={employmentStatusFilter}
        onEmploymentStatusChange={setEmploymentStatusFilter}
        activeFilter={activeFilter}
        onActiveFilterChange={setActiveFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters()}
        totalCount={pagination.total}
        isMobile={isMobile}
      />

      <FormDialog
        open={showForm}
        onClose={resetForm}
        title={editingTeacher ? "Edit Teacher" : "Add New Teacher"}
        onSubmit={handleSubmit}
        saving={saving}
        isMobile={isMobile}
      >
        <TeacherForm
          formData={formData}
          onFormChange={updateFormData}
          onSubmit={handleSubmit}
          isEditing={!!editingTeacher}
        />
      </FormDialog>

      <TeacherDetailsDialog
        open={!!viewingTeacher}
        onClose={() => setViewingTeacher(null)}
        teacher={viewingTeacher}
        isMobile={isMobile}
      />

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        teacher={teacherToDelete}
        deleting={deleting}
      />

      <Dialog open={showCreatedTeacherDialog} onClose={() => setShowCreatedTeacherDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
          Teacher Created Successfully!
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Save these credentials! The password won't be shown again.
          </Alert>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {createdTeacherCredentials.loginType}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body1" fontWeight="medium">
                {createdTeacherCredentials.loginId}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(createdTeacherCredentials.loginId);
                  setSnackbar({ open: true, message: `${createdTeacherCredentials.loginType} copied!`, severity: 'success' });
                }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Temporary Password
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" fontWeight="bold" color="primary">
                {createdTeacherCredentials.password}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(createdTeacherCredentials.password);
                  setSnackbar({ open: true, message: 'Password copied!', severity: 'success' });
                }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Teacher will be required to change this password on first login.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreatedTeacherDialog(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center">
              <PeopleIcon color="primary" sx={{ mr: 1 }} />
              Teachers List
            </Typography>
          </Box>

          <TeachersTable
            teachers={teachers}
            onView={handleView}
            onEdit={openForm}
            onDelete={handleDelete}
          />

          {pagination.last_page > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {pagination.total === 0 ? 0 : (pagination.current_page - 1) * pagination.per_page + 1} to{" "}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} entries
              </Typography>
              <Pagination
                count={pagination.last_page}
                page={pagination.current_page}
                onChange={(event, page) => fetchTeachers(page)}
                color="primary"
                size={isMobile ? "small" : "medium"}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
      
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeachersRefactored;
